import React, { useState, useEffect, useCallback } from 'react';
import { Download, X, RefreshCw } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const PWAInstallPrompt: React.FC = () => {
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstall, setShowInstall] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallEvent(e as BeforeInstallPromptEvent);
      const lastDismissed = localStorage.getItem('jarvis-pwa-dismissed');
      if (lastDismissed) {
        const diff = Date.now() - parseInt(lastDismissed);
        if (diff < 7 * 24 * 60 * 60 * 1000) return;
      }
      setShowInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    (window as any).__jarvisUpdateReady = () => {
      setShowUpdate(true);
    };

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (!installEvent) return;
    await installEvent.prompt();
    const result = await installEvent.userChoice;
    if (result.outcome === 'accepted') {
      setShowInstall(false);
      setInstallEvent(null);
    }
  }, [installEvent]);

  const handleDismiss = useCallback(() => {
    setShowInstall(false);
    setDismissed(true);
    localStorage.setItem('jarvis-pwa-dismissed', String(Date.now()));
  }, []);

  const handleUpdate = useCallback(() => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
    }
    window.location.reload();
  }, []);

  if (showUpdate) {
    return (
      <div className="fixed bottom-4 right-4 z-[9999] max-w-sm animate-in slide-in-from-bottom duration-300">
        <div className="bg-[#0F172A] text-white rounded-xl shadow-2xl border border-[#1E293B] p-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#0D9488] flex items-center justify-center flex-shrink-0">
              <RefreshCw className="w-4.5 h-4.5 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-[13px] font-semibold">Update Available</p>
              <p className="text-[12px] text-[#94A3B8] mt-0.5">A new version of Jarvis is ready. Refresh to update.</p>
            </div>
            <button onClick={() => setShowUpdate(false)} className="text-[#64748B] hover:text-white p-1">
              <X className="w-4 h-4" />
            </button>
          </div>
          <button
            onClick={handleUpdate}
            className="w-full mt-3 py-2 bg-[#0D9488] hover:bg-[#0B7A70] text-white text-[13px] font-semibold rounded-lg transition-colors"
          >
            Refresh Now
          </button>
        </div>
      </div>
    );
  }

  if (!showInstall || dismissed) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[9999] max-w-sm animate-in slide-in-from-bottom duration-300">
      <div className="bg-[#0F172A] text-white rounded-xl shadow-2xl border border-[#1E293B] p-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#0D9488] flex items-center justify-center flex-shrink-0">
            <Download className="w-4.5 h-4.5 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-[13px] font-semibold">Install Jarvis</p>
            <p className="text-[12px] text-[#94A3B8] mt-0.5">Add Jarvis to your desktop for quick access. Works offline for cached pages.</p>
          </div>
          <button onClick={handleDismiss} className="text-[#64748B] hover:text-white p-1">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex gap-2 mt-3">
          <button
            onClick={handleInstall}
            className="flex-1 py-2 bg-[#0D9488] hover:bg-[#0B7A70] text-white text-[13px] font-semibold rounded-lg transition-colors"
          >
            Install
          </button>
          <button
            onClick={handleDismiss}
            className="px-4 py-2 text-[#94A3B8] hover:text-white text-[13px] font-medium rounded-lg border border-[#334155] hover:border-[#475569] transition-colors"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
